<template>
  <Section :id="id" :header="t('common.billTos')" class="bill-to-section">
    <div
      class="relative"
      :class="{ 'cursor-pointer': editable }"
      @click="onAddressClick"
    >
      <NamedAddress
        :name="name"
        :address="address"
        :class="{ 'cursor-pointer': editable }"
      />

      <FontAwesomeIcon
        v-if="editable"
        aria-hidden="true"
        class="edit-icon absolute top-0 right-0"
        :icon="['fas', 'pencil']"
      />
    </div>

    <AddressBookModal
      v-model:visible="isShowingAddressBookModal"
      v-model:search-criteria="searchCriteria"
      type="billTo"
      :header="t('common.addParam', { param: t('common.billTos') })"
      :search-results="searchResults"
      data-testid="bill-to-section-address-book-modal"
      @select="onLocationSelect"
    />

    <BillToEditModal
      :id="`${id}-bill-to-edit-modal`"
      v-model:visible="isShowingEditModal"
      :bill-to="internalBillTo"
      :editable="editable"
      @save="onUpdateBillTo"
      @show-address-book-modal="onSwitchModals"
    />
  </Section>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Address, BillToDetails, Location } from '@rygen/rygen-ui/model';

import { useBillTos } from '@/composables';
import {
  BillTo,
  EmbeddableLocation,
  OrderBillTo,
  ShipmentBillTo,
} from '@/model';

import BillToEditModal from '@/components/location/BillToEditModal.vue';
import AddressBookModal from '@rygen/rygen-ui/components/address/AddressBookModal.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  billTo: {
    type: Object as PropType<BillTo>,
    default: () => new OrderBillTo(),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, billTo, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:bill-to', billTo: BillTo): void;
}>();

const { resetSearch, searchCriteria, searchResults } = useBillTos();
const { t } = useI18n();

const internalBillTo: Ref<BillTo> = ref(new OrderBillTo());
const isShowingAddressBookModal = ref(false);
const isShowingEditModal = ref(false);

const name = computed(() => billTo.value.location?.locationName ?? '');
const address = computed(
  () => billTo.value.location?.toAddress() ?? new Address(),
);

const onAddressClick = () => {
  if (editable.value) {
    isShowingEditModal.value = true;
  }
};

const onUpdateBillTo = (updatedBillTo: BillTo) => {
  const Clazz = Object.getPrototypeOf(billTo.value).constructor;
  internalBillTo.value = new Clazz(updatedBillTo);
};

const onSwitchModals = () => {
  isShowingEditModal.value = false;
  nextTick(() => (isShowingAddressBookModal.value = true));
};

const onLocationSelect = async (location: Location) => {
  if (!location.id) {
    return;
  }

  internalBillTo.value.location = EmbeddableLocation.fromLocation(location);
  internalBillTo.value.billToDetails = new BillToDetails(
    location.billToDetails,
  );
};

watch(
  billTo,
  (newVal: BillTo) => {
    if (isEqual(internalBillTo.value, newVal)) {
      return;
    }

    const Clazz = OrderBillTo.isOrderBillTo(newVal)
      ? OrderBillTo
      : ShipmentBillTo;
    internalBillTo.value = new Clazz(newVal);
  },
  { deep: true, immediate: true },
);

watch(
  internalBillTo,
  (newVal: BillTo) => {
    if (!isEqual(billTo.value, newVal)) {
      emit('update:bill-to', newVal);
    }
  },
  { deep: true },
);

watch(isShowingAddressBookModal, (_newVal: boolean) => {
  resetSearch();
});
</script>

<style>
.bill-to-section {
  .named-address {
    .named-address__address-line,
    .named-address__zone-information,
    .named-address__country {
      color: var(--p-gray-400);
    }

    &:hover + .edit-icon {
      opacity: 1;
    }
  }

  .edit-icon {
    margin-right: var(--sp-3);
    opacity: 0;
    pointer-events: none;
    transition: opacity 100ms ease-in;
  }
}
</style>
